import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable, usePermanentLocations, usePermanentRealtimeDealers } from "components";
import { useRegisterNavAction } from "hooks";
import { DeleteTyreModal, EditTyreModal } from "modules/IncompleteTyresDashboard/components";
import "modules/IncompleteTyresDashboard/components/IncompleteTyresTable/IncompleteTyresTable.scss";
import { handlePageSizeChange, handlePaginationChange, useIncompleteTyresFilters, useIncompleteTyresQuery } from "modules/IncompleteTyresDashboard/hooks";
import { getTyreSeasonName } from "modules/IncompleteTyresDashboard/util";
import { formatDate } from "utils/dateHelperFunctions";

import type { AccessorColumnDef } from "@tanstack/react-table";
import type { Tyre } from "models";

export const IncompleteTyresTable = () => {
  const { t } = useTranslation();

  const [tyreToUpdate, setTyreToUpdate] = useState<Tyre | null>(null);
  const [tyreToDelete, setTyreToDelete] = useState<Tyre | null>(null);

  const { dealers } = usePermanentRealtimeDealers();
  const { locations } = usePermanentLocations();
  const filters = useIncompleteTyresFilters();
  const { tyres, nbItems, nbPages, refetch: refetchIncompleteTyres, isFetching: isIncompleteTyresFetching } = useIncompleteTyresQuery();
  useRegisterNavAction(() => refetchIncompleteTyres());

  const getDealerAndLocation = (dealerLocationId?: number) => {
    const location = locations.find(loc => loc.id === dealerLocationId);
    if (!location) return "";

    const dealer = dealers.find(d => d.id === location.dealer_id);
    return dealer ? `${dealer.name} - ${location.name}` : location.name;
  };

  const columns = useMemo<AccessorColumnDef<Tyre>[]>(
    () => [
      {
        id: "created_on",
        size: 100,
        accessorKey: "created_on",
        header: () => <span className="no-wrap-text">{t("v8_creation_date", "Creation date")}</span>,
        cell: ({ row: { original } }) => formatDate(original.created_on, DATE_FORMATS_FNS.dateMonthYear)
      },
      {
        id: "manufacturer",
        size: 180,
        accessorKey: "manufacturer",
        header: () => <span className="no-wrap-text">{t("v8_manufacturer", "Manufacturer")}</span>,
        cell: ({ row: { original } }) => original.manufacturer
      },
      {
        id: "width",
        size: 50,
        accessorKey: "width",
        header: () => <span className="no-wrap-text">{t("v8_width", "Width")}</span>,
        cell: ({ row: { original } }) => original.width
      },
      {
        id: "height",
        size: 50,
        accessorKey: "height",
        header: () => <span className="no-wrap-text">{t("v8_height", "Height")}</span>,
        cell: ({ row: { original } }) => original.height
      },
      {
        id: "size",
        size: 50,
        accessorKey: "size",
        header: () => <span className="no-wrap-text">{t("v8_size", "Size")}</span>,
        cell: ({ row: { original } }) => original.size
      },
      {
        id: "speed",
        size: 50,
        accessorKey: "speed",
        header: () => <span className="no-wrap-text">{t("v8_speed", "Speed")}</span>,
        cell: ({ row: { original } }) => original.speed
      },
      {
        id: "season",
        size: 120,
        accessorKey: "season",
        header: () => <span className="no-wrap-text">{t("v8_season", "Season")}</span>,
        cell: ({ row: { original } }) => getTyreSeasonName(original.season, t)
      },
      {
        id: "description",
        size: 300,
        accessorKey: "description",
        header: () => <span className="no-wrap-text">{t("v8_description", "Description")}</span>,
        cell: ({ row: { original } }) => original.description
      },
      {
        id: "created_by",
        size: 150,
        accessorFn: row => row,
        header: () => <span className="no-wrap-text">{t("v8_created_by", "Created by")}</span>,
        cell: ({ row: { original } }) => (original.created_by ? `${original.created_by.first_name} ${original.created_by.last_name}` : "")
      },
      {
        id: "location",
        size: 200,
        accessorFn: row => row,
        header: () => <span className="no-wrap-text">{t("v8_location", "Location")}</span>,
        cell: ({ row: { original } }) => getDealerAndLocation(original.created_by?.dealer_location_id)
      },
      {
        id: "actions",
        size: 40,
        accessorFn: row => row,
        header: () => null,
        cell: ({ row: { original: tyre } }) => (
          <div className="actions-container">
            <div className="action-edit-button-container" onClick={() => setTyreToUpdate(tyre)}>
              <Icon className="icon pen to square edit-icon action-button-icon-center" />
            </div>
            <div className="action-button-container" onClick={() => setTyreToDelete(tyre)}>
              <Icon className="trash action-button-icon-center" color="red" />
            </div>
          </div>
        )
      }
    ],
    [t, dealers, locations]
  );

  return (
    <>
      <div className="IncompleteTyresTable">
        <ReactTable
          isFetching={isIncompleteTyresFetching}
          columns={columns}
          data={tyres}
          pagination={{ pageIndex: filters.page - 1, pageSize: filters.page_size }}
          pageCount={nbPages}
          totalItems={nbItems}
          pageSize={filters.page_size}
          onPaginationChange={handlePaginationChange}
          onChangePageSize={handlePageSizeChange}
        />
      </div>
      {tyreToUpdate && <EditTyreModal tyre={tyreToUpdate} onClose={() => setTyreToUpdate(null)} />}
      <DeleteTyreModal onClose={() => setTyreToDelete(null)} tyre={tyreToDelete} />
    </>
  );
};
